import { Play, ExternalLink, Check, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";

export default function CardTile({ card, onToggleCheck, onEdit, onDelete, onRemoveFromSpace }) {
  const { type } = card;
  const hasImg = !!card.image_url;
  const hasLink = !!card.url;
  const hasList = card.checklist && card.checklist.length > 0;

  const Menu = () => (
    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
      <DropdownMenu>
        <DropdownMenuTrigger data-testid={`card-menu-${card.card_id}`} className="w-7 h-7 grid place-items-center rounded-full bg-black/50 backdrop-blur text-white/80 hover:text-white">
          <MoreHorizontal size={14} />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-[color:var(--surface)] border-white/10">
          {onEdit && <DropdownMenuItem onClick={() => onEdit(card)} data-testid={`card-edit-${card.card_id}`}><Pencil size={13} className="mr-2" /> Edit</DropdownMenuItem>}
          {onRemoveFromSpace && <DropdownMenuItem onClick={() => onRemoveFromSpace(card)}>Remove from space</DropdownMenuItem>}
          {onDelete && <DropdownMenuItem onClick={() => onDelete(card)} data-testid={`card-delete-${card.card_id}`} className="text-red-400 focus:text-red-300"><Trash2 size={13} className="mr-2" /> Delete</DropdownMenuItem>}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  // Quote-only card
  if (type === "quote" && !hasImg && !hasList && !hasLink) {
    return (
      <div className="card-base p-6 relative group" data-testid={`card-${card.card_id}`}>
        <Menu />
        <div className="serif text-4xl leading-none mb-2" style={{ color: "var(--orange)" }}>“</div>
        <p className="serif-italic text-lg leading-relaxed">{card.content}</p>
        {card.title && <div className="text-xs text-white/50 mt-3">— {card.title}</div>}
      </div>
    );
  }

  // Pure image
  if (type === "image" && hasImg && !card.content && !hasList) {
    return (
      <div className="card-base overflow-hidden relative group" data-testid={`card-${card.card_id}`}>
        <Menu />
        <img src={card.image_url} alt={card.title || ""} className="w-full object-cover" />
        {card.title && <div className="p-3 text-sm text-white/70">{card.title}</div>}
      </div>
    );
  }

  // Pure link
  if (type === "link" && hasLink && !hasImg && !card.content && !hasList) {
    const host = (() => { try { return new URL(card.url).hostname.replace("www.", ""); } catch { return ""; } })();
    return (
      <div className="card-base p-4 relative group" data-testid={`card-${card.card_id}`}>
        <Menu />
        <a href={card.url} target="_blank" rel="noreferrer" className="block">
          <div className="flex items-center gap-2 text-xs text-white/50 mb-2"><ExternalLink size={12} /> {host}</div>
          <div className="text-white font-medium">{card.title || card.url}</div>
        </a>
      </div>
    );
  }

  // Rich / mixed note (default): may contain any combination
  return (
    <div className="card-base relative group overflow-hidden" data-testid={`card-${card.card_id}`}>
      <Menu />
      {hasImg && <img src={card.image_url} alt="" className="w-full object-cover max-h-72" />}
      <div className="p-5 space-y-3">
        {card.title && <div className="font-medium">{card.title}</div>}
        {card.content && (
          type === "quote"
            ? <p className="serif-italic text-lg leading-relaxed"><span className="serif text-3xl leading-none mr-1" style={{ color: "var(--orange)" }}>“</span>{card.content}</p>
            : <p className="text-sm text-white/85 whitespace-pre-wrap">{card.content}</p>
        )}
        {hasLink && (() => {
          const host = (() => { try { return new URL(card.url).hostname.replace("www.", ""); } catch { return card.url; } })();
          return (
            <a href={card.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs text-white/60 hover:text-white">
              <ExternalLink size={12} /> {host}
            </a>
          );
        })()}
        {hasList && (
          <ul className="space-y-2">
            {card.checklist.map(item => (
              <li key={item.id} className="flex items-center gap-2 text-sm">
                <button onClick={() => onToggleCheck?.(card, item)} className="w-4 h-4 rounded-full border border-white/30 grid place-items-center">
                  {item.done && <Check size={11} style={{ color: "var(--orange)" }} />}
                </button>
                <span className={item.done ? "line-through text-white/40" : "text-white/85"}>{item.text}</span>
              </li>
            ))}
          </ul>
        )}
        {card.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {card.tags.map(t => <span key={t} className="text-[11px] text-white/50">#{t}</span>)}
          </div>
        )}
      </div>
    </div>
  );
}
